import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import { useTheme } from '@mui/material/styles';
import { Host } from '@proposify/mfe-tools';
import { selectContentId, selectContentType } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { useContentMetadata } from 'components/editor/hooks/useContentMetadata';
import { useUserContext } from 'providers/UserProvider';
import { lazy, Suspense } from 'react';
import type { GetDocumentResponse } from 'services/documents/DocumentRepository';
import type { UserEntity } from 'services/entities/UserEntity';
import type { DocumentStatus } from 'services/documents/entities';
import { useSearchParams } from 'hooks/useSearchParams';
import { INSIGHTS_DOC_ID_KEY } from './InsightsButton';

const InsightsModule = lazy(() => import('core-management-remote/insights'));

const Container = ({ children }: { children: React.ReactNode }) => <Box>{children}</Box>;

type InsightsInjectableDocument = {
  id: string;
  contentId: string;
  title: string;
  isLocked: boolean;
  status: DocumentStatus;
};

type InsightsHostProps = {
  user: UserEntity;
  documents: InsightsInjectableDocument[];
  surface: 'editor' | 'preview';
};

export default function InsightsHost({ user, documents, surface }: InsightsHostProps) {
  const theme = useTheme();
  const [searchParams] = useSearchParams();

  if (!searchParams.get(INSIGHTS_DOC_ID_KEY)) return null;

  return (
    <Host name="insights" injectables={{ user, theme, documents, surface }}>
      <Suspense fallback={<Container>{null}</Container>}>
        <Fade in={true} timeout={600}>
          <Box>
            <InsightsModule />
          </Box>
        </Fade>
      </Suspense>
    </Host>
  );
}

export function EditorInsights() {
  const contentId = useAppSelector(selectContentId);
  const contentType = useAppSelector(selectContentType);
  const { data: documentData } = useContentMetadata(contentType, contentId);
  const { data: user } = useUserContext();

  if (!user || !documentData?.documentId || !documentData.title || documentData.isLocked == null || !documentData.status) {
    return null;
  }

  const injectables = {
    user,
    surface: 'editor' as const,
    documents: [
      {
        id: documentData.documentId,
        contentId,
        title: documentData.title,
        isLocked: documentData.isLocked,
        status: documentData.status,
      },
    ],
  };

  return <InsightsHost user={user} documents={injectables.documents} surface={injectables.surface} />;
}

export function PreviewInsights({ user, documentData }: { user: UserEntity | undefined; documentData: GetDocumentResponse | undefined }) {
  if (!user || !documentData?.documentId || !documentData.title || documentData.isLocked == null || !documentData.status) {
    return null;
  }

  const injectables = {
    user,
    surface: 'preview' as const,
    documents: [
      {
        id: documentData.documentId,
        contentId: documentData.contentId,
        title: documentData.title,
        isLocked: documentData.isLocked,
        status: documentData.status,
      },
    ],
  };

  return <InsightsHost user={user} documents={injectables.documents} surface={injectables.surface} />;
}
